"use client"

import { useErrorCapture } from "@/lib/hooks/useErrorCapture"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { AlertTriangle } from "lucide-react"
import { StatusPageHeader, SuggestionsList } from "./"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

interface ErrorProps {
  error: Error & { digest?: string }
  /**
   * Name of the error page, eg 'home-page'. This is logged in Sentry.
   */
  name: string

  /**
   * Title of the page.
   */
  title?: string

  /**
   * Description of the page.
   */
  description?: string

  children?: React.ReactNode
}

const ERROR_SUGGESTIONS = [
  "Refresh the page - sometimes that's all it takes",
  "Come back in a few minutes",
  "Clear your browser cache and cookies",
  "Reach out to support@paragraph.com if this persists",
]

/**
 * ErrorPage component that handles application-wide errors with context-aware messaging
 * Following Paragraph design system and styling patterns
 */
export default function ErrorPage({
  error,
  name,
  title = "Something went wrong",
  description = "We apologize, but something went wrong on our servers. This isn't your fault - it's on us.",
  children,
}: ErrorProps): JSX.Element {
  useErrorCapture(error, name)

  const handleRefresh = () => {
    window.location.reload()
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 sm:px-6 md:px-12">
      <Card className="max-w-lg w-full border shadow">
        <CardHeader className="pb-0">
          <StatusPageHeader
            title={title}
            description={description}
            icon={AlertTriangle}
            iconColor="text-red-600"
            iconBgColor="bg-red-100"
          />
        </CardHeader>

        <CardContent className="pt-6">
          <div className="space-y-6">
            <SuggestionsList
              title="What you can try:"
              suggestions={ERROR_SUGGESTIONS}
            />

            <div className="rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
              <h2 className={cn("text-sm font-medium text-foreground")}>
                Error details
              </h2>
              <p className="mt-2 text-sm text-muted-foreground font-mono overflow-x-auto">
                {error.message || "Unknown error"}
              </p>
              {error.digest && (
                <p className="mt-1 text-xs text-muted-foreground font-mono">
                  Error ID: {error.digest}
                </p>
              )}
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex justify-center gap-4 pt-2">
          {children || (
            <Button onClick={handleRefresh} className="min-w-[120px]">
              Refresh Page
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
